import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { FilingDeadlineDto } from '@signalvote/shared';
import { DeadlineStatus } from '@signalvote/shared';
import { useFinanceDeadlines } from '@/hooks/queries/useFinanceDeadlines';
import { cn } from '@/lib/utils';
import { fmtDateShort, fmtDateLong, daysUntil, dueTimeSuffix } from '../utils';
import { FilingExposureNotes } from '../FilingExposureNotes';

/**
 * Zone 3 (treasurer redesign 2026-07-21) — the ambient "Upcoming filings" strip.
 *
 * Replaces the old UpcomingFilingsCard with one quiet slate-50 line that is
 * always visible: "Upcoming filings · {count} — next: {name}, {short date}".
 * Clicking expands a grey list of the upcoming deadlines underneath, with the
 * existing "View all →" link to the reports list.
 *
 * This zone is NEVER red. Overdue filings live exclusively in the ranked action
 * queue (Zone 2); this strip carries only DeadlineStatus.Upcoming items in a
 * neutral slate tone regardless of how near they are. Collapsed by default.
 */

// Short cadence chip derived from the deadline's own strings (never inferred).
function cadenceLabel(name: string, scheduleName: string | null): string | null {
  const s = `${scheduleName ?? ''} ${name}`.toLowerCase();
  if (s.includes('weekly')) return 'WEEKLY';
  if (s.includes('monthly')) return 'MONTHLY';
  if (s.includes('quarterly')) return 'QUARTERLY';
  if (s.includes('semi')) return 'SEMI-ANNUAL';
  if (s.includes('annual')) return 'ANNUAL';
  if (s.includes('pre-election') || s.includes('pre election')) return 'PRE-ELECTION';
  return null;
}

export function UpcomingFilingsStrip() {
  const { data: deadlines } = useFinanceDeadlines({
    status: DeadlineStatus.Upcoming,
    pageSize: 8,
  });
  const [open, setOpen] = useState(false);

  // A deadline's stored status is stamped when the calendar is generated and never moves as
  // days pass, so a missed report still arrives as Upcoming. Past-due ones belong to the
  // overdue notice, never here. Due-date order puts them first in the page, so subtracting
  // the dropped rows keeps the count right.
  const listed = deadlines?.items ?? [];
  const upcoming = listed.filter((d) => daysUntil(d.dueDate) >= 0);
  if (upcoming.length === 0) return null;

  const count = (deadlines?.totalCount ?? listed.length) - (listed.length - upcoming.length);
  const next = upcoming[0];

  return (
    <div
      data-testid="upcoming-filings-strip"
      className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"
    >
      <button
        type="button"
        data-testid="upcoming-filings-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="text-[13px] text-slate-600">
          <span className="font-medium">Upcoming filings</span>
          <span className="text-slate-400"> · {count}</span>
          <span className="text-slate-500">
            {' '}
            — next: {next.name}, {fmtDateShort(next.dueDate)}
            {dueTimeSuffix(next.dueTime)}
          </span>
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        )}
      </button>

      {open && (
        <div className="mt-2 flex flex-col gap-0.5">
          {upcoming.map((d) => (
            <UpcomingStripRow key={d.id} deadline={d} />
          ))}
          <Link
            to="/admin/finance/reports"
            className="mt-1.5 inline-flex items-center gap-1 self-start px-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            View all <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

function UpcomingStripRow({ deadline }: { deadline: FilingDeadlineDto }) {
  const days = daysUntil(deadline.dueDate);
  const cadence = cadenceLabel(deadline.name, deadline.scheduleName);

  return (
    <Link
      to="/admin/finance/reports"
      search={{ deadlineId: deadline.id }}
      data-testid={`upcoming-strip-row-${deadline.id}`}
      className={cn(
        'flex items-center justify-between gap-3 rounded px-1 py-1.5 hover:bg-slate-100',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-slate-700">
          {deadline.name}
        </span>
        {cadence && (
          <span className="block text-[10px] font-semibold tracking-[.06em] text-slate-400 font-mono">
            {cadence}
          </span>
        )}
        <FilingExposureNotes deadline={deadline} testIdPrefix="upcoming" as="span" size="xs" />
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-xs font-bold font-mono text-slate-500">
          {days >= 0 ? `${days}d` : `${Math.abs(days)}d`}
        </span>
        <span className="block text-[10.5px] text-slate-400 font-mono">
          {fmtDateLong(deadline.dueDate)}
          {dueTimeSuffix(deadline.dueTime)}
        </span>
      </span>
    </Link>
  );
}
